import { DeskPaginationComponents, DeskPaginationProps } from '@my-types';
import { Styled, React } from '@common/3rd';
import { StyledComponents } from '@styles';
import { ModelWidget, DeskComponentRegistry } from '@component';

class DeskPagination<P extends DeskPaginationProps, S, C extends DeskPaginationComponents> extends ModelWidget<
	P,
	S,
	C
> {
	protected initComponents(): C {
		return {
			Box: Styled.div.attrs({
				'data-widget': 'desk-pagination'
			})`
				display: flex;
				> *:hover {
					transform: scale(1.1);
				}

				${props => this.renderCSS(props.theme)}
			`,
			Arrow: Styled(StyledComponents.FAS).attrs({
				'data-widget': 'desk-pagination-button'
			})`
				height: ${props => props.theme.BTN_HEIGHT}px;
				line-height: ${props => props.theme.BTN_HEIGHT}px;
				width: ${props => props.theme.BTN_HEIGHT}px;
				font-size: ${props => props.theme.FONT_SIZE_L2}px;
				color: ${props => props.theme.FONT_COLOR_LIGHT2};
				cursor: pointer;
			` as any,
			Button: Styled.a.attrs({
				'data-widget': 'desk-pagination-button'
			})`
				text-align: center;
				height: ${props => props.theme.BTN_HEIGHT}px;
				line-height: ${props => props.theme.BTN_HEIGHT}px;
				min-width: ${props => props.theme.BTN_HEIGHT}px;
				font-size: ${props => props.theme.FONT_SIZE_SMALL}px;
				font-weight: ${props => props.theme.FONT_WEIGHT_BOLD};
				color: ${props => props.theme.FONT_COLOR_LIGHT2};
				cursor: pointer;
			`,
			TotalCount: Styled.div.attrs({
				'data-widget': 'desk-pagination-total-count'
			})`
				text-align: center;
				height: ${props => props.theme.BTN_HEIGHT}px;
				line-height: ${props => props.theme.BTN_HEIGHT}px;
				min-width: ${props => props.theme.BTN_HEIGHT}px;
				font-size: ${props => props.theme.FONT_SIZE_SMALL}px;
				font-weight: ${props => props.theme.FONT_WEIGHT_BOLD};
				color: ${props => props.theme.FONT_COLOR_LIGHT2};
				margin-right: ${props => props.theme.MARGIN}px;
			`
		} as C;
	}
	/**
	 * 显示3个页码
	 */
	renderPages(): JSX.Element[] {
		const pagination = this.getValueFromModel();
		let { pageNumber = 0, pageCount = 1 } = pagination || ({} as any);
		// 后端返回数据从0开始计数
		pageNumber++;

		let pages;
		switch (pageCount) {
			case 0:
			case 1:
				pages = [1];
				break;
			case 2:
				pages = [1, 2];
				break;
			default:
				if (pageNumber === 1) {
					pages = [1, 2, 3];
				} else if (pageNumber === pageCount) {
					pages = [pageCount - 2, pageCount - 1, pageCount];
				} else {
					pages = [pageNumber - 1, pageNumber, pageNumber + 1];
				}
		}
		const C = this.getComponents();
		return pages.map((page, index) => {
			const isCurrent = page === pageNumber;
			const text = isCurrent ? `${page}/${pageCount}` : `${page}`;
			return (
				<C.Button
					key={index}
					onClick={() => {
						this.onPageClicked(page);
					}}
				>
					{text}
				</C.Button>
			);
		});
	}
	render() {
		const C = this.getComponents();
		return (
			<C.Box>
				{this.renderTotalCount()}
				<C.Arrow icon="angle-double-left" onClick={this.onFirstPageClicked} />
				<C.Arrow icon="angle-left" onClick={this.onPreviousPageClicked} />
				{this.renderPages()}
				<C.Arrow icon="angle-right" onClick={this.onNextPageClicked} />
				<C.Arrow icon="angle-double-right" onClick={this.onLastPageClicked} />
			</C.Box>
		);
	}
	renderTotalCount() {
		const C = this.getComponents();
		const pagination = this.getValueFromModel();
		let { totalCount } = pagination || ({} as any);
		if (totalCount) {
			return (
				<C.TotalCount>
					总数：{totalCount}笔
				</C.TotalCount>
			);
		} else {
			return;
		}
	} 
	onPageClicked = (pageNumber: number): void => {
		this.onPageChanged(pageNumber);
	};
	onPreviousPageClicked = (): void => {
		let { pageNumber = 0 } = this.getValueFromModel() || ({} as any);
		// 后端返回数据从0开始计数
		switch (pageNumber + 1) {
			case 1:
				// 如果当前页是第一页, 不需要触发
				break;
			case 2:
				// 如果当前页是第二页
				this.onPageChanged(1);
				break;
			case 3:
				// 如果当前页是第三页, 则显示的是2, 3, 4.
				this.onPageChanged(2);
				break;
			case 4:
				// 如果当前页为第四页, 则显示的是3, 4, 5.
				// 前翻一页之后显示页为1, 2, 3, 即跳到第二页
				this.onPageChanged(3);
				break;
			default:
				// 如果当前页为第五页之后,
				// 前翻一页显示页为回退三页
				// 后端返回数据从0开始计数
				this.onPageChanged(pageNumber + 1 - 3);
		}
	};
	onNextPageClicked = (): void => {
		let { pageNumber = 0, pageCount = 1 } = this.getValueFromModel() || ({} as any);
		// 后端返回数据从0开始计数
		switch (pageNumber + 1) {
			case pageCount:
				// 如果当前页是倒数一页, 不需要触发
				break;
			case pageCount - 1:
				// 如果当前页是倒数第二页, 则显示的是 倒数3, 倒数2, 倒数1.
				// 后翻一页之后显示页为 倒数3, 倒数2, 倒数1, 即跳到倒数第二页
				this.onPageChanged(pageCount);
				break;
			case pageCount - 2:
				// 如果当前页是倒数第三页, 则显示的是 倒数4, 倒数3, 倒数2.
				// 后翻一页之后显示页为 倒数3, 倒数2, 倒数1, 即跳到倒数第二页
				this.onPageChanged(pageCount - 1);
				break;
			case pageCount - 3:
				// 如果当前页为倒数第四页, 则显示的是 倒数5, 倒数4, 倒数3.
				// 后翻一页之后显示页为 倒数3, 倒数2, 倒数1, 即跳到倒数第二页
				this.onPageChanged(pageCount - 2);
				break;
			default:
				// 如果当前页为倒数第五页之前,
				// 后翻一页显示页为前进三页
				// 后端返回数据从0开始计数
				this.onPageChanged(pageNumber + 1 + 3);
		}
	};
	onLastPageClicked = (): void => {
		const { pageCount = 1 } = this.getValueFromModel() || ({} as any);
		this.onPageChanged(pageCount);
	};
	onFirstPageClicked = (): void => {
		this.onPageChanged(1);
	};
	/**
	 * 这个方法的参数页码从1开始计数
	 */
	onPageChanged(toPageNumber: number): void {
		let { pageNumber = 0, pageCount = 1, pageSize = 10 } = this.getValueFromModel() || ({} as any);
		// 后端返回数据从0开始计数
		if (pageNumber + 1 === toPageNumber) {
			// 跳过去的就是当前页, 不需要跳转
			// console.debug('Page changed event ignored.');
			return;
		}

		if (this.props.onPageChanged) {
			// 发送出去的页码回归从0开始计数
			this.props.onPageChanged.call(this, {
				pageNumber: toPageNumber - 1,
				pageCount,
				pageSize,
				invoker: this
			});
		}
	}
}

DeskComponentRegistry.register({
	pagination: DeskPagination
});

export { DeskPagination };
